/*
 * Zero-dependency SRP email probe — decides which branch the email-first login
 * takes WITHOUT sending a password. It fires the FIRST leg of USER_SRP_AUTH
 * (InitiateAuth) and reads the outcome, then NEVER answers the PASSWORD_VERIFIER
 * challenge: no password is sent, no token is minted, and the account's
 * failed-sign-in counter is NOT incremented (that only advances on a wrong
 * RespondToAuthChallenge, which we never send).
 *
 * Raw fetch only (no amazon-cognito-identity-js) so the probe is tiny and
 * independently testable; fetchImpl is injectable, matching the oidc.ts idiom.
 */
import { readAuthConfig } from './authConfig'

export type ProbeResult = 'exists' | 'no-account' | 'unconfirmed' | 'reset-required' | 'indeterminate'

/** A large random hex SRP_A. Cognito accepts any well-formed SRP_A for the
 *  InitiateAuth leg; because we never complete the challenge, its
 *  cryptographic correctness is irrelevant. */
function randomSrpA(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(256))
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

/** Extract the AWS `__type` short name from a cognito-idp JSON error body
 *  (handles both "Foo" and "com.amazonaws...#Foo" forms). */
function errorType(body: string): string {
  try {
    const t = (JSON.parse(body) as { __type?: string }).__type ?? ''
    return t.split('#').pop() ?? ''
  } catch {
    return ''
  }
}

export async function probeEmail(email: string, fetchImpl: typeof fetch = fetch): Promise<ProbeResult> {
  const config = readAuthConfig()
  if (!config) return 'indeterminate'

  let res: Response
  try {
    res = await fetchImpl(`https://cognito-idp.${config.region}.amazonaws.com/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
      },
      body: JSON.stringify({
        AuthFlow: 'USER_SRP_AUTH',
        ClientId: config.userPoolClientId,
        AuthParameters: { USERNAME: email, SRP_A: randomSrpA() },
      }),
    })
  } catch {
    // Network/CORS failure — cannot decide; caller degrades to the PASSWORD step.
    return 'indeterminate'
  }

  if (res.ok) {
    const json = (await res.json().catch(() => ({}))) as { ChallengeName?: string }
    // A challenge back means the account exists and SRP reached the verifier
    // step (we STOP here — no password, no token).
    return json.ChallengeName === 'PASSWORD_VERIFIER' ? 'exists' : 'indeterminate'
  }

  const detail = await res.text().catch(() => '')
  switch (errorType(detail)) {
    case 'UserNotFoundException':
      return 'no-account'
    case 'UserNotConfirmedException':
      return 'unconfirmed'
    case 'PasswordResetRequiredException':
      return 'reset-required'
    case 'NotAuthorizedException':
      // USER_SRP_AUTH disabled on the app-client — a build/infra misconfig, not
      // a user state. Surface as an error (spec); the UI degrades gracefully.
      throw new Error('SRP auth flow is not enabled on the consumer app-client.')
    default:
      return 'indeterminate'
  }
}
